"use client";

import { cn } from "@/lib/utils";
import { X, Menu as MenuIcon, ChevronDown } from "lucide-react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from "motion/react";
import Link from "next/link";
import React, { useRef, useState } from "react";
import { MenuItem, ProductItem } from "./navbar-menu.tsx";
import HoveredLink from "src/components/HoveredLink";
import type { 
  NavbarProps,
  NavBodyProps,
  NavItemsProps,
  MobileNavProps,
  MobileNavHeaderProps,
  MobileNavMenuProps,
  NavItemType,
} from "./types.js";
import HoverBg from "../HoverBg";
import { useSiteMetadata } from "@/hooks/useContent";


export const Navbar = ({ children, className }: NavbarProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const [visible, setVisible] = useState<boolean>(false);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 100) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  });

  return (
    <motion.div
      ref={ref}
      className={cn("sticky inset-x-0 top-5 z-40 w-full", className)}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(
              child as React.ReactElement<{ visible?: boolean }>,
              { visible },
            )
          : child,
      )}
    </motion.div>
  );
};

export const NavBody = ({ children, className, visible }: NavBodyProps) => {
  return (
    <motion.div
      animate={{
        backdropFilter: visible ? "blur(10px)" : "none",
        boxShadow: visible
          ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
          : "none",
        width: visible ? "40%" : "100%",
        y: visible ? 20 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 50,
      }}
      style={{
        minWidth: "1000px",
      }}
      className={cn(
        "relative z-[60] mx-auto hidden w-full max-w-6xl flex-row items-center justify-between self-start rounded-xl bg-transparent px-4 py-2 lg:flex",
        visible && "bg-white/80",
        className,
      )}
    >
      {children}
    </motion.div>
  );
};

export const NavItems = ({ items, className, onItemClick, setActive, active }: NavItemsProps) => {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <motion.div
      onMouseLeave={() => setHovered(null)}
      className={cn(
        "absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium text-neutral-600 transition duration-200 hover:text-primary-600 lg:flex lg:space-x-2",
        className,
      )}
    >
      {items.map((item, idx) => (
        <div
          onMouseEnter={() => setHovered(idx)}
          className="relative px-4 py-2 text-neutral-600"
          key={`link-${idx}`}
        >
          {hovered === idx && <HoverBg layoutId="Navbar-hovered" />}

          {item.submenu && item.submenu.links.length > 0 ? (
            <MenuItem 
              setActive={(setActive)}
              active={active}
              item={item.name}
            >
                <div
                  className={cn(
                    "gap-4",
                    item.submenu?.type === "col"
                      ? "flex flex-col"
                      : "grid grid-cols-1"
                  )}
                >
                  {item.submenu.links.map((subItem) => (
                    item.submenu?.type === "col" ? (
                        <HoveredLink
                          key={subItem.name}
                          href={subItem.link}
                        >
                          <span className="relative z-30">{subItem.name}</span>
                        </HoveredLink>
                      ) : (
                        <ProductItem
                          key={subItem.name}
                          href={subItem.link}
                          src={subItem.src || ""}
                          title={subItem.name}
                          description={subItem.description}
                        />
                      )
                    ))}
                </div>
            </MenuItem>
          ) : (
            <LinkItem
              item={item}
              setActive={setActive}
              onItemClick={() => onItemClick?.()}
            />
          )}
        </div>
      ))}
    </motion.div>
  );
};

interface LinkItemProps {
  item: NavItemType;
  onItemClick: () => void;
  setActive: (item: string | null) => void;
}

const LinkItem = ({ item, onItemClick, setActive }: LinkItemProps) => {
  return (
    <Link 
      onClick={onItemClick} 
      href={item.link} 
      onMouseEnter={() => setActive(null)} 
      onMouseLeave={() => setActive(item.name)}
    >
      <span className="relative z-30">{item.name}</span>
    </Link>
  );
};

export const MobileNav = ({ children, className, visible }: MobileNavProps) => {
  return (
    <motion.div
      animate={{
        backdropFilter: visible ? "blur(10px)" : "none",
        boxShadow: visible
          ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
          : "none",
        width: visible ? "95%" : "100%",
        paddingRight: visible ? "12px" : "0px",
        paddingLeft: visible ? "12px" : "0px",
        borderRadius: visible ? "4px" : "2rem",
        y: visible ? 20 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 50,
      }}
      className={cn(
        "relative z-50 mx-auto flex w-full max-w-[calc(100vw-1.5rem)] flex-col items-center justify-between bg-transparent px-0 py-2 lg:hidden",
        visible && "bg-white/80",
        className,
      )}
    >
      {children}
    </motion.div>
  );
};

export const MobileNavItem = ({ items, className }: {items: NavItemType[], className?: string}) => {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className={cn("flex flex-col items-start justify-start gap-4", className)}>
      {items.map((item, idx) => (
        <div
          className="relative w-full"
          key={`link-${idx}`}
        >
          {item.submenu && item.submenu.links.length > 0 ? (
            <DropdownMenu item={item} open={open} setOpen={setOpen} />
          ) : (
            <div className="px-4 py-2 text-neutral-600">
              <LinkItem
                item={item}
                setActive={() => {}}
                onItemClick={() => {}}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const DropdownMenu = ({ item, className, open, setOpen }: { item: NavItemType, className?: string, open: string | null, setOpen: (open: string | null) => void }) => {  
  return (
    <div className={cn("flex flex-col items-start justify-start", className)}>
      <button
        onClick={() => setOpen(open === item.name ? null : item.name)}
        className="flex w-full items-center justify-between px-2 py-1 text-left text-neutral-600 hover:text-neutral-800 transition-colors text-sm"
      >
        <span className="relative z-30">{item.name}</span>
        <motion.div
          animate={{ rotate: open === item.name ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="ml-2"
        >
          <ChevronDown className="size-4" />
        </motion.div>
      </button>
      
      <AnimatePresence>
        {open === item.name && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full overflow-hidden pl-4 max-h-52 overflow-y-auto"
          >
            <div className="flex flex-col gap-2 py-2">
              {item.submenu?.links.map((subItem) => (
                <HoveredLink
                  key={subItem.name}
                  href={subItem.link}
                  className="px-4 py-2 text-sm text-neutral-500"
                >
                  <span className="relative z-30">{subItem.name}</span>
                </HoveredLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const MobileNavHeader = ({
  children,
  className,
}: MobileNavHeaderProps) => {
  return (
    <div
      className={cn(
        "flex w-full flex-row items-center justify-between",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const MobileNavMenu = ({
  children,
  className,
  isOpen,
}: MobileNavMenuProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            "absolute inset-x-0 top-16 z-50 flex w-full flex-col items-start justify-start gap-4 rounded-lg bg-white px-4 py-4 lg:py-8 shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]",
            className,
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const MobileNavToggle = ({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) => {
  return isOpen ? (
    <X className="text-black" onClick={onClick} />
  ) : (
    <MenuIcon className="text-black" onClick={onClick} />
  );
};

export const NavbarLogo = () => {
  const siteMetadata = useSiteMetadata() as {
    logo: {
      text: string;
      image: string;
    };
  };

  return (
    <Link
      href="/"
      className="relative z-20 mr-4 flex items-center space-x-2 px-2 py-1 text-sm font-normal text-black"
    >
      {/* TODO: Add logo */}
      {/* <Image
        src={siteMetadata?.logo?.image || "/logo.png"}
        alt="logo"
        width={30}
        height={30}
      /> */}
      <span className="text-3xl font-medium text-black">
        {siteMetadata?.logo?.text || "ClaireChains"}
      </span>
    </Link>
  );
};

export const NavbarButton = ({
  href,
  as: Tag = "a",
  children,
  className,
  variant = "primary",
  ...props
}: {
  href?: string;
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
  variant?: "primary";
} & (
  | React.ComponentPropsWithoutRef<"a">
  | React.ComponentPropsWithoutRef<"button">
)) => {
  const baseStyles =
    "px-4 py-2 rounded-md bg-white button bg-white text-black text-sm font-bold relative cursor-pointer hover:-translate-y-0.5 transition duration-200 inline-block text-center";

  const variantStyles = {
    primary:
      "bg-gradient-to-br from-primary-500 to-secondary-500 text-white hover:from-primary-600 hover:to-secondary-600 hover:shadow-lg hover:shadow-primary-500/20",
  };

  return (
    <Tag
      href={href || undefined}
      className={cn(baseStyles, variantStyles[variant], className)}
      {...props}
    >
      {children}
    </Tag>
  );
};